import ArrowDropDownRounded from '@mui/icons-material/ArrowDropDownRounded'
import ArrowDropUpRounded from '@mui/icons-material/ArrowDropUpRounded'
import Language from '@mui/icons-material/Language'
import { Menu, MenuItem, Stack, Typography } from '@mui/material'
import { StyledSecondaryActionButton } from 'App/Header/Header.styles'
import React, { useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import useSelectedLanguage from '../../App/hooks/useSelectedLanguage'

interface LanguageChooserProps {
  onLanguageChange?: () => void
  compact?: boolean
  withBorder?: boolean
}

const LanguageChooser: React.FC<LanguageChooserProps> = ({
  onLanguageChange,
  compact = false,
  withBorder = false,
}) => {
  const { selectedLanguage, setSelectedLanguage } = useSelectedLanguage()
  const { t } = useTranslation()

  const iconRef = useRef<SVGSVGElement>(null)
  const arrowRef = useRef<HTMLDivElement>(null)

  const [anchorEl, setAnchorEl] = useState<
    null | HTMLDivElement | SVGSVGElement
  >(null)
  const handleClick = () => {
    setAnchorEl(compact ? iconRef.current : arrowRef.current)
  }

  const handleClose = () => setAnchorEl(null)

  const handleLanguageChange = (language: 'sv' | 'en') => {
    if (selectedLanguage !== language) {
      setSelectedLanguage(language)
      if (onLanguageChange) {
        onLanguageChange()
      }
      handleClose()
    }
  }

  const isMenuOpen = Boolean(anchorEl)
  const buttonId = useId()
  const menuId = useId()

  return (
    <>
      <StyledSecondaryActionButton
        id={buttonId}
        compact={compact}
        withBorder={withBorder}
        onClick={handleClick}
        data-testid={
          compact
            ? 'language-chooser-button-compact'
            : 'language-chooser-button'
        }
        aria-haspopup="true"
        aria-expanded={isMenuOpen}
        aria-controls={isMenuOpen ? menuId : undefined}
        aria-label={t(`translation:common.selectLanguage`) as string}
      >
        <Language ref={iconRef} />
        {!compact && (
          <>
            <Typography variant="body1">
              {t(`translation:common.language.${selectedLanguage}`) as string}
            </Typography>
            <Stack ref={arrowRef}>
              {isMenuOpen ? (
                <ArrowDropUpRounded data-testid="language-arrow-up" />
              ) : (
                <ArrowDropDownRounded data-testid="language-arrow-down" />
              )}
            </Stack>
          </>
        )}
      </StyledSecondaryActionButton>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleClose}
        data-testid="language-menu"
        anchorOrigin={{
          vertical: 36,
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: compact ? 'center' : 'right',
        }}
        MenuListProps={{
          'aria-labelledby': buttonId,
        }}
      >
        <MenuItem
          onClick={() => handleLanguageChange('sv')}
          data-testid="language-menu-sv"
          selected={selectedLanguage === 'sv'}
        >
          {t('translation:common.language.sv') as string}
        </MenuItem>
        <MenuItem
          onClick={() => handleLanguageChange('en')}
          data-testid="language-menu-en"
          selected={selectedLanguage === 'en'}
        >
          {t('translation:common.language.en') as string}
        </MenuItem>
      </Menu>
    </>
  )
}

export default LanguageChooser
